import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Wifi, Shield, Phone, Zap, ArrowRight, Check, Star, Smartphone, Globe, Signal } from "lucide-react";

const features = [
  { icon: Wifi, title: "Unlimited Talk, Text, Data", desc: "No caps, no throttling. True unlimited on a premium network." },
  { icon: Shield, title: "Premium 5G Network", desc: "Full access to one of America's largest 5G networks for reliable coverage." },
  { icon: Phone, title: "Keep Your Number", desc: "Free port-in supported. Bring your number with you seamlessly." },
  { icon: Zap, title: "Fast Activation", desc: "SIM ships to your door. Activate online in minutes." },
];
const included = ["Unlimited 5G/LTE Data", "Unlimited Talk & Text", "Mobile Hotspot", "Wi-Fi Calling", "Free Number Porting", "Free SIM Card", "No Annual Contract", "International Texting"];

const HERO_BG = "https://images.unsplash.com/photo-1753185869653-66fc6137db36?w=1400&q=80&auto=format";
const LIFESTYLE_1 = "https://images.unsplash.com/photo-1714978444512-91e54c2de6cc?w=1200&q=80&auto=format";
const LIFESTYLE_2 = "https://images.unsplash.com/photo-1770462594767-c64faffea172?w=1200&q=80&auto=format";
const LIFESTYLE_3 = "https://images.unsplash.com/photo-1758876200754-17a09a6c2728?w=1200&q=80&auto=format";
const COVERAGE_MAP = "https://customer-assets.emergentagent.com/job_slugs-124/artifacts/95ftw1b5_image.png";
const SATELLITE_IMG = "https://static.prod-images.emergentagent.com/jobs/dce75fd1-7329-4d0b-80a6-0ec3f66e09ee/images/8d303c0590e032d69a58f8bec3ad439a910c13a506d1e1a3b5a26fcd6a92fe0e.png";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="bg-[#050505]">
      {/* Hero Section with full-width background */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-8 backdrop-blur-sm">
              <Star className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#D4AF37] text-sm font-manrope font-medium">Premium Wireless Service</span>
            </div>
            <h1 className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#F5F5F5] leading-[1.05] mb-4">
              Unlimited Phone Service for <span className="text-[#D4AF37]">$50/month</span>
            </h1>
            <p className="text-[#A0A0A0] text-lg sm:text-xl font-manrope leading-relaxed mb-3 max-w-lg">
              Bring your own device. No contracts. No hidden fees.
            </p>
            <p className="text-[#D4AF37]/70 text-sm font-manrope mb-10">$35 one-time activation fee applies</p>
            <div className="flex flex-wrap gap-4">
              <Link to={user ? "/dashboard" : "/signup"} className="group bg-[#D4AF37] hover:bg-[#F0C541] text-black px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-yellow-500/20 inline-flex items-center gap-3" data-testid="hero-cta-button">
                Get Started <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#plans" className="border border-white/20 hover:border-[#D4AF37] text-[#F5F5F5] px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm" data-testid="hero-view-plan">View Plan</a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#F5F5F5] text-center mb-4">Why Prestigious Wireless?</h2>
          <p className="text-[#A0A0A0] text-center mb-14 text-lg font-manrope max-w-2xl mx-auto">Premium wireless without the premium price.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-8 hover:border-[#D4AF37]/50 transition-all duration-300 hover:-translate-y-1 group" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors"><f.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="font-outfit text-xl font-semibold text-[#F5F5F5] mb-2">{f.title}</h3>
                <p className="text-[#A0A0A0] font-manrope text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Map Section */}
      <section id="coverage" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-6">
              <Globe className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#D4AF37] text-sm font-manrope font-medium">Nationwide Network</span>
            </div>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">Nationwide Coverage You Can Count On</h2>
            <p className="text-[#A0A0A0] text-lg font-manrope max-w-2xl mx-auto">Powered by one of the largest 5G networks in the country.</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl shadow-[#D4AF37]/5">
            <img src={COVERAGE_MAP} alt="Nationwide 5G coverage map" className="w-full h-auto" loading="lazy" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#050505] to-transparent h-24" />
            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-md border border-[#2A2A2A] rounded-lg px-3 py-1.5">
              <p className="text-[#D4AF37] text-xs font-manrope font-medium">5G Network Coverage</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: Signal, text: "Reliable coverage across major cities, suburbs, and highways" },
              { icon: Zap, text: "Fast 5G speeds where available" },
              { icon: Globe, text: "Seamless nationwide connectivity" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <p className="text-[#A0A0A0] font-manrope text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          {/* Satellite Services */}
          <div className="mt-14 bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-outfit text-2xl font-bold text-[#F5F5F5] mb-3">Satellite Connectivity</h3>
                <p className="text-[#A0A0A0] font-manrope leading-relaxed mb-4">
                  Even beyond traditional cell towers, advanced satellite-to-mobile technology extends your reach. Send texts, access key apps, and stay connected in remote outdoor areas where conventional coverage doesn't reach — from hiking trails to open waters.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  {["Text messaging in remote areas", "Key app connectivity outdoors", "Emergency communication support", "Growing satellite network coverage"].map((f, i) => (
                    <div key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" /><span className="text-[#F5F5F5] font-manrope text-sm">{f}</span></div>
                  ))}
                </div>
                <p className="text-[#A0A0A0]/50 text-xs font-manrope italic">
                  Satellite features not available on all phones. Requires compatible device and clear view of the sky. Coverage, speed, and availability may vary by location. Not all features available in all areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Satellite Connectivity Section */}
      <section className="relative overflow-hidden py-0">
        <div className="relative min-h-[70vh] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <img src={SATELLITE_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-[#050505]/50" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-6 py-24">
            <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] leading-tight mb-6">
              If you can see the sky, you're <span className="text-[#D4AF37]">connected.</span>
            </h2>
            <p className="text-[#A0A0A0] text-lg sm:text-xl font-manrope leading-relaxed mb-4 max-w-2xl mx-auto">
              From remote locations to everyday life, advanced network technology keeps you connected in more places than ever before.
            </p>
            <p className="text-[#A0A0A0]/50 text-xs font-manrope mb-10 max-w-lg mx-auto">
              Coverage and performance may vary by location and device. Not all features available in all areas.
            </p>
            <a href="#coverage" className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F0C541] text-black px-8 py-3.5 rounded-full font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-yellow-500/20">
              Check Coverage <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Lifestyle Section 1 — Friends using phones */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={LIFESTYLE_1} alt="Friends staying connected" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
          </div>
          <div>
            <h2 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-4">Stay Connected. Stay Together.</h2>
            <p className="text-[#A0A0A0] text-lg font-manrope leading-relaxed mb-6">
              Whether you're catching up with friends, streaming your favorite shows, or video calling family — Prestigious Wireless keeps you connected with truly unlimited data.
            </p>
            <Link to={user ? "/dashboard" : "/signup"} className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope font-semibold hover:text-[#F0C541] transition-colors">
              Start today <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Plan Section */}
      <section id="plans" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#F5F5F5] text-center mb-4">One Plan. Everything Included.</h2>
          <p className="text-[#A0A0A0] text-center mb-14 text-lg font-manrope">Simple, transparent pricing. No surprises.</p>
          <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-8 sm:p-12 border-b border-[#2A2A2A]">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                <div><h3 className="font-outfit text-2xl font-bold text-[#F5F5F5] mb-2">Prestigious Wireless Unlimited</h3><p className="text-[#A0A0A0] font-manrope">Everything you need in one plan</p></div>
                <div className="text-left sm:text-right">
                  <div className="flex items-baseline gap-1"><span className="font-outfit text-5xl font-bold text-[#D4AF37]">$50</span><span className="text-[#A0A0A0] font-manrope">/month</span></div>
                  <p className="text-[#A0A0A0] text-sm font-manrope mt-1">+ $35 activation fee (one-time)</p>
                </div>
              </div>
            </div>
            <div className="p-8 sm:p-12">
              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {included.map((item, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`plan-feature-${i}`}>
                    <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-[#D4AF37]" /></div>
                    <span className="text-[#F5F5F5] font-manrope">{item}</span>
                  </div>
                ))}
              </div>
              <Link to={user ? "/dashboard" : "/signup"} className="block w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black text-center px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300" data-testid="plan-cta-button">
                {user ? "Go to Dashboard" : "Get Started - $85 Due Today"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Section 2 — Outdoor relaxed */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <h2 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-4">Freedom Without Limits</h2>
            <p className="text-[#A0A0A0] text-lg font-manrope leading-relaxed mb-6">
              Take your service anywhere. With nationwide T-Mobile coverage and no data caps, enjoy your phone the way it was meant to be used — everywhere.
            </p>
            <div className="space-y-3">
              {["No data throttling", "Reliable 5G/LTE speeds", "Hotspot included"].map((item, i) => (
                <div key={i} className="flex items-center gap-3"><Check className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#F5F5F5] font-manrope">{item}</span></div>
              ))}
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3] order-1 lg:order-2">
            <img src={LIFESTYLE_2} alt="Using phone outdoors" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Lifestyle Section 3 — Couple / Trusted */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={LIFESTYLE_3} alt="Couple enjoying connectivity" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
          </div>
          <div>
            <h2 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-4">Trusted by Real People</h2>
            <p className="text-[#A0A0A0] text-lg font-manrope leading-relaxed mb-6">
              Prestigious Wireless is built for people who want simple, reliable service without the games. Real plans, real savings, real service.
            </p>
            <Link to={user ? "/dashboard" : "/signup"} className="bg-[#D4AF37] hover:bg-[#F0C541] text-black px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-all duration-300" data-testid="lifestyle-cta">
              Join Today <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BYOD Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8 sm:p-12">
          <div className="flex items-center gap-4 mb-6"><Smartphone className="w-8 h-8 text-[#D4AF37]" /><h2 className="font-outfit text-2xl font-bold text-[#F5F5F5]">Bring Your Own Device (BYOD)</h2></div>
          <div className="grid sm:grid-cols-2 gap-6">
            {["Phone must be unlocked", "Compatible with T-Mobile network", "Works with most modern smartphones", "No need to buy a new device"].map((item, i) => (
              <div key={i} className="flex items-center gap-3"><Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" /><span className="text-[#A0A0A0] font-manrope">{item}</span></div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
